import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text } from '@common-fe/common-fe';

import { FilterRecord, ReportTab, ReportTypeKeys } from '../../report.types';
import {
  REPORT_TYPE_TITLES,
  ScheduledFormPayload,
  ScheduledItemFilters,
  ScheduledReportPayload,
} from '../ScheduledReport.types';

import { useCreateScheduledReportQuery } from './queries';
import ScheduledReportForm from './ScheduledReportForm';
import ScheduledReportPreview from './ScheduledReportPreview';

interface Props extends Partial<ScheduledReportPayload> {
  type?: ReportTypeKeys;
  isEditMode?: boolean;
  defaultEmployerId?: string;
  onClose: () => void;
  onSuccess: () => void;
  onSetPreviewShowed?: (value: boolean) => void;
}
interface FormPayload extends ScheduledFormPayload {
  filterValues: Partial<ScheduledItemFilters>;
}
const ScheduledReportSetup: React.FC<Props> = ({
  onClose,
  onSuccess,
  isEditMode,
  onSetPreviewShowed,
  defaultEmployerId,

  ...props
}) => {
  const { isLoading, handleSave } = useCreateScheduledReportQuery({
    id: props.id,
    isEditMode,
  });
  const [currentTab, setCurrentTab] = useState(props.tab || ReportTab.Report);
  const [savedValues, setSavedValues] = useState<Partial<FormPayload>>({ ...props });
  const [localValues, setLocalValues] = useState<Partial<FormPayload>>({});
  const [filterValues, setFilterValues] = useState<Partial<FilterRecord>>({ ...props.filterValues });

  const [isPreviewShowed, setPreviewShowed] = useState(false);
  useEffect(() => {
    if (!isEditMode) {
      return;
    }
    if (currentTab === props.tab) {
      setFilterValues({ ...props.filterValues });
    } else {
      setFilterValues({
        employerId: filterValues?.employerId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, props.tab, currentTab]);
  useEffect(() => {
    if (onSetPreviewShowed) {
      onSetPreviewShowed(isPreviewShowed);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewShowed]);
  const currentTitle = useMemo(() => {
    if (!savedValues.type) {
      return '';
    }
    return REPORT_TYPE_TITLES[savedValues.type] || '';
  }, [savedValues.type]);
  const handleNext = useCallback((value: ScheduledReportPayload) => {
    setLocalValues(value);
    setSavedValues(value);
    setPreviewShowed(true);
  }, []);
  const handleBack = useCallback(() => {
    setPreviewShowed(false);
  }, []);
  const handleSubmit = useCallback(async () => {
    await handleSave({
      id: props.id,
      employerId: props.employerId,
      employerIds: props.employerIds,
      ...(localValues as FormPayload),
      filterValues: filterValues as FilterRecord,
    });
    onSuccess();
    onClose();
  }, [
    filterValues,
    handleSave,
    localValues,
    onClose,
    onSuccess,
    props.employerId,
    props.employerIds,
    props.id,
  ]);

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return `Edit ${props.name} Schedule`;
    }
    if (!currentTitle) {
      return 'Add a new Schedule';
    }
    return `Add a Schedule for ${currentTitle}`;
  }, [currentTitle, isEditMode, props.name]);

  const handleSetType = useCallback(
    (newType: ReportTypeKeys) => {
      setSavedValues({
        ...savedValues,
        type: newType,
      });
    },
    [savedValues]
  );
  return (
    <Box direction="column">
      <Text textAlign="center" size="2xl" weight={700}>
        {modalTitle}
      </Text>
      {isPreviewShowed && savedValues.type ? (
        <ScheduledReportPreview
          isEditMode={isEditMode}
          isLoading={isLoading}
          defaultEmployerId={defaultEmployerId}
          defaultFilterValues={filterValues}
          type={savedValues.type}
          tab={savedValues.tab}
          onChangeFilters={setFilterValues}
          onBack={handleBack}
          onNext={handleSubmit}
        />
      ) : (
        <ScheduledReportForm
          isEditMode={isEditMode}
          onChangeType={handleSetType}
          onChangeTab={setCurrentTab}
          defaultValues={savedValues}
          {...props}
          onNext={handleNext}
          onClose={onClose}
          isLoading={isLoading}
        />
      )}
    </Box>
  );
};
export default ScheduledReportSetup;
